import Forms from './../../utils/forms/forms';
import { mapGetters, mapMutations } from 'vuex';
import orderProductService from './../../services/orderProduct';
import billProductService from './../../services/billProduct';
import taxService from './../../services/tax';
import creditNoteProductService from './../../services/creditNoteProduct';
import productDepositService from './../../services/productDeposit';
import store from './../../store';
import organizationSettingsService from './../../services/organizationSettings';
import bulkAddProductModal from './../../components/bulkAddProductModal/bulkAddProductModal.vue';

export default {
  data() {
    return {
      form: new Forms({
        ref: {
          value: '',
          type: 'link',
          attributes: {
            href: '',
            target: '',
            fieldClass: '',
          },
        },
        billProductId: {
          value: '',
          type: 'autocomplete',
          attributes: {
            isSearch: true,
            searchMethod: this.searchMethod,
            loading: true,
          },
        },
        price: {
          value: '',
          type: 'number',
        },
        productDepositName: {
          value: '',
          type: 'plaintext',
        },
        quantity: {
          value: '',
          type: 'number',
        },
        discount: {
          value: '',
          type: 'number',
        },
        taxId: {
          value: '',
          type: 'select',
        },
      }),
      destroyLoading: false,
      addingProduct: false,
      editingProduct: false,
      error: false,
      ready: false,
      columnWidths: { ref: '150px' },
    };
  },

  props: {
    /**
     * The current order.
     */
    order: {
      type: Object,
      required: true,
    },

    /**
     * The current credit note.
     */
    bill: {
      type: Object,
      required: true,
    },

    /**
     * The current order.
     */
    billRoundInvoiceTotal: {
      type: Boolean,
      required: false,
    },
  },

  computed: {
    ...mapGetters({
      products: 'allProducts',
      orderProducts: 'allOrderProducts',
      billProducts: 'allBillProducts',
      creditNoteProducts: 'allCreditNoteProducts',
      taxes: 'allTaxes',
      productDeposits: 'allProductDeposits',
    }),

    additionalButtons() {
      const buttons = [{
        text: this.$t('table.orderProducts.addProductsButton'),
        action: this.showBulkAddProductModal,
      }];
      if (this.bill && this.bill.creditNoteSource) {
        buttons.push({
          text: this.$t('table.orderProducts.addAllBillProductsButton'),
          action: this.addAllBillProductsFromBill,
        });
      }
      return buttons;
    },

    creditProductsList() {
      const links = {};
      const products = this.creditNoteProducts.map((p) => {
        let deposit = [];
        if (p.productDepositId) {
          deposit = this.productDeposits.all
          .filter(d => p.productDepositId.toString() === d.id.toString());
        }

        let value = '';
        if (deposit.length) {
          value = `Deposit: ${deposit[0].name}`;
        }
        const billProduct = this.billProducts.filter(product => (
          p.billProductId !== undefined && product.id.toString() === p.billProductId.toString()
        ))[0] || {};

        const addClass = `link-table-${p.refNumber}-${p.refId}`;
        let linkDisable = false;
        if (billProduct.refNumber === '--') linkDisable = true;

        links[p.id] = p.refType === 'bill' ? `#/bills/${p.refId}/edit` : `#/orders/${p.refId}/edit`;

        this.form.setExtraAttr('ref', {
          href: links[p.id],
          hrefs: links,
          target: '_blank',
          fieldClass: addClass,
          fieldDisable: linkDisable,
        });
        const label = p.refType === 'bill' ? this.$t('static.referenceSign.invoice') : this.$t('static.referenceSign.order');

        const row = {
          productDepositName: value,
          price: billProduct.price,
          quantity: billProduct.quantity,
          taxId: billProduct.taxId,
          discount: billProduct.discount,
          ref: (p.refNumber === '--') ? '--' : `${label}-${p.refNumber}`,
        };
        // Prefer API line tax %; if absent, same idea as backend (bill_product vs order line) via store.
        let taxDisplayPct;
        if (p.taxPercentage !== undefined && p.taxPercentage !== null) {
          taxDisplayPct = p.taxPercentage;
        } else if (billProduct.taxPercentage !== undefined && billProduct.taxPercentage !== null) {
          taxDisplayPct = billProduct.taxPercentage;
        }
        if (taxDisplayPct !== undefined && taxDisplayPct !== null) {
          row.taxIdDisplay = taxDisplayPct;
        }
        return Object.assign(row, p);
      });

      return products;
    },

    totalsTableList() {
      const fmt = val => Number(val).toLocaleString('nb-NO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const result = [
        {
          name: this.$t('static.orders.edit.subtotal'),
          value: fmt(this.subtotal),
        },
        {
          name: this.$t('static.orders.edit.tax'),
          value: fmt(this.tax),
        },
      ];

      if (this.discount > 0) {
        result.push({
          name: this.$t('static.orders.edit.discount'),
          value: fmt(this.discount),
        });
      }

      if (this.deposit > 0) {
        result.push({
          name: this.$t('static.orders.edit.deposit'),
          value: fmt(this.deposit),
        });
      }

      result.push({
        name: this.$t('static.orders.edit.total'),
        value: fmt(this.total),
      });

      return result;
    },

    /**
     * If bill status is sent or higher.
     */
    isBillSent() {
      return this.bill.billStatus !== 'Draft' &&
             this.bill.billStatus !== 'Pending';
    },

    /**
     * Whether table was shown in a bill and round invoice total is true
     */
    isShownOnBill() {
      return this.billRoundInvoiceTotal;
    },

    /**
     * Whether table should appear or not.
     */
    showTable() {
      if (this.creditProductsList.length > 0 || this.addingProduct || this.editingProduct) {
        return true;
      }

      return false;
    },

    subtotal() {
      return this.creditProductsList.map(p => (
        p.price * p.quantity
      )).reduce((a, b) => a + b, 0)
      .toFixed(2);
    },

    discount() {
      return this.creditProductsList.map((p) => {
        const bp = this.getBillProduct(p.billProductId);
        if (bp == null) {
          return 0;
        }
        return (p.price ? p.price : bp.price) * p.quantity * (bp.discount / 100);
      }).reduce((a, b) => a + b, 0).toFixed(2);
    },

    /* eslint max-len: ["error", 150] */
    tax() {
      return this.creditProductsList.map((p) => {
        const bp = this.getBillProduct(p.billProductId);

        const orderProductId = bp ? bp.orderProductId : p.orderProductId;
        if (bp == null && !orderProductId) {
          return 0;
        }
        const op = this.getOrderProduct(orderProductId);
        const discount = bp ? bp.discount : op.discount;
        const taxPercentage = bp && bp.taxId ? bp.taxPercentage : op.taxPercentage;
        return (p.price ? p.price : bp.price) * p.quantity * (1 - (discount / 100)) * ((taxPercentage) / 100);
      }).reduce((a, b) => a + b, 0).toFixed(2);
    },

    deposit() {
      return this.creditProductsList.map(p => (
        p.orderProductDeposit * p.quantity
      )).reduce((a, b) => a + b, 0)
      .toFixed(2);
    },

    total() {
      const total = (Number(this.subtotal) + Number(this.deposit)) - Number(this.discount);
      const totalTax = total + Number(this.tax);

      if (this.isShownOnBill) {
        return Math.round(totalTax);
      }

      return totalTax.toFixed(2);
    },

    /**
     * The rounding for the total.
     */
    rounding() {
      const total = Number(this.subtotal - this.discount);
      const totalTax = total + Number(this.tax);
      const roundedDecimal = (totalTax % 1).toFixed(2);

      if (roundedDecimal < 0.50) {
        return -roundedDecimal;
      }

      return roundedDecimal;
    },
  },
  created() {
    Promise.all([organizationSettingsService.show()]).then((data) => {
      this.form.setDisplays('billProductId', data[0].productInfoDisplay);
    });
  },
  watch: {
    /**
     * Watch product select for autocomplete other fields with defaults.
     */
    'form.billProductId': function () {
      const billProduct = this.form.billProductId;
      const productId = typeof this.form.billProductId.id !== 'undefined' ? this.form.billProductId.id : this.form.billProductId;

      if (typeof billProduct !== 'object') {
        return '';
      }
      if (productId > 0) {
        const tax = this.taxes.all.filter(t => (
          t.id === billProduct.taxId
        ));

        // console.log(tax);
        if (tax.length > 0) {
          this.form.taxId = tax[0].id;
        }

        this.form.price = billProduct.price;
        if (this.form.quantity <= 0) {
          this.form.quantity = billProduct.quantity;
        }
        //
      }
    },

    /**
    * Watches state update to inject on Forms class.
    *
    * @param  {Object} products    The order products list.
    */
    billProducts(products) {
      // console.log(products);
      this.form.setOptions('billProductId', products);
    },

    creditNoteProducts(products) {
     // // console.log(products);
      // this.form.setOptions('billProductId', products);
    },
    /**
    * Watches state update to inject on Forms class.
    *
    * @param  {Object} products    The products list.
    */
    productDeposits(deposits) {
      this.form.setOptions('productDepositId', deposits.all);
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} taxes    The taxes list.
     */
    taxes(taxes) {
      this.form.setOptions('taxId', taxes.all.map(tax => ({ id: tax.id, name: tax.taxPercentage })));
    },
  },

  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
    }),

    showBulkAddProductModal() {
      this.setModalContent(() => bulkAddProductModal);
      this.setModalProps({
        fetchProductsMethod: this.searchMethod,
        onSave: this.handleBulkProductSave,
      });
      this.toggleModal(true);
    },

    handleBulkProductSave(creditNoteProducts) {
      const id = this.bill.id;
      const data = creditNoteProducts.map(p => ({
        billProductId: p.id,
        quantity: p.qty,
        price: p.price,
      }));
      return creditNoteProductService.bulkStore(id, data)
        .then(() => {
          this.form.loading = false;
        });
    },

    addAllBillProductsFromBill() {
      if (!this.bill.creditNoteSource) {
        return;
      }
      const existingIds = new Set(
        this.creditNoteProducts
          .map(p => p.billProductId)
          .filter(id => id != null && id !== '')
          .map(id => String(id)),
      );
      const toAdd = this.billProducts.filter(
        bp => !existingIds.has(String(bp.id)),
      );
      if (toAdd.length === 0) {
        this.$message({
          showClose: true,
          message: this.$t('table.orderProducts.addAllBillProductsNothing'),
          type: 'info',
        });
        return;
      }
      this.form.loading = true;
      const data = toAdd.map(p => ({
        billProductId: p.id,
        quantity: p.quantity,
        price: p.price,
      }));
      creditNoteProductService.bulkStore(this.bill.id, data)
        .then(() => {
          this.form.loading = false;
          this.$message({
            showClose: true,
            message: this.$t('table.orderProducts.addAllBillProductsSuccess', { count: toAdd.length }),
            type: 'success',
          });
        })
        .catch((errors) => {
          this.form.loading = false;
          let msg = this.$t('table.orderProducts.addAllBillProductsError');
          if (errors && errors.exceed_creditnote_amount && errors.exceed_creditnote_amount[0]) {
            msg = errors.exceed_creditnote_amount[0];
          } else if (errors && errors.exceedCreditnoteAmount && errors.exceedCreditnoteAmount[0]) {
            msg = errors.exceedCreditnoteAmount[0];
          } else if (errors && errors.message) {
            msg = errors.message;
          }
          this.$message({
            showClose: true,
            message: msg,
            type: 'error',
          });
        });
    },

    getDepositValue(id) {
      const deposits = this.productDeposits.all.filter(d => d.id.toString() === id.toString());
      return deposits.length ? deposits[0].amount : 0;
    },

    /**
     * Get the order product for a credit note product.
     *
     * @param {Number} productId The orderProductId.
     */
    getBillProduct(productId) {
      if (this.billProducts.length <= 0) {
        const p = this.orderProducts.filter(p => (
          p.id.toString() === productId.toString()
        ));

        if (p.length > 0) {
          return p[0];
        }
        return null;
      }
      const p = this.billProducts.filter(p => (
            p.id.toString() === productId.toString()
          ));

      if (p.length > 0) {
        return p[0];
      }
      return null;
    },

    /**
     * Get the order product for a credit note product.
     *
     * @param {Number} productId The orderProductId.
     */
    getOrderProduct(productId) {
      // console.log(this.orderProducts)
      return this.orderProducts.filter(p => (
        p.id.toString() === productId.toString()
      ))[0];
    },

    /**
     * This will be called when the form emit a change event.
     *
     * @param  {String} name    The input name.
     * @param  {String} value    The input value.
     */
    onFormChange(name, value) {
      this.form.clearSuccess(name, value);
      this.form.clearErrors(name);
      this.form[name] = value;
      this.$forceUpdate();
    },

    /**
     * Opens the form to add a product.
     */
    addProduct() {
      this.cancelForm();
      this.addingProduct = true;
    },

    /**
     * Action for editing a order product.
     */
    edit(product) {
      this.cancelForm();
      this.editingProduct = product.id;
      this.form.assignData(product);
    },

    /**
     * Closes the form, canceling inputs.
     */
    cancelForm() {
      this.form.billProductId = '';
      this.form.quantity = '';
      this.addingProduct = false;
      this.editingProduct = false;
    },

    /**
     * Updates a order product.
     */
    update(billProductId) {
      const me = this;
      this.form.loading = true;
      const id = this.bill.id;
      const data = this.form.data();
      data.billProductId = typeof data.billProductId.id !== 'undefined' ? data.billProductId.id : data.billProductId;

      if (data.quantity.toString() === '0') {
        me.form.loading = false;
        me.$message({
          showClose: true,
          message: me.$t('static.orders.edit.quantityError'),
          type: 'error',
        });
        return Promise.all();
      }

      if (data.price.toString() === '0') {
        me.form.loading = false;
        me.$message({
          showClose: true,
          message: me.$t('static.orders.edit.priceError'),
          type: 'error',
        });
        return Promise.all();
      }

      if (data.billProductId.toString() === '') {
        me.form.loading = false;
        me.$message({
          showClose: true,
          message: me.$t('static.orders.edit.productErrorMessage'),
          type: 'error',
        });
        return Promise.all();
      }

      const creditNoteData = creditNoteProductService.update(id, data, billProductId)
      .then(() => {
        me.form.loading = false;
        creditNoteProductService.all(id);
        data.billProduct = me.billProducts.filter(p => (
          p.id.toString() === data.billProductId.toString()
        ))[0].name;
        store.dispatch('updateCreditNoteProduct', { creditNoteProduct: data, id: me.editingProduct });
        me.cancelForm();
      }).catch((errors) => {
        if (errors.exceedCreditnoteAmount) {
          me.$message({
            showClose: true,
            message: errors.exceedCreditnoteAmount[0],
            type: 'error',
          });
        }

        me.form.loading = false;
        me.form.recordErrors(errors);
        const errorsKeys = Object.keys(me.form.errors.errors);
        me.$message({
          showClose: true,
          message: errors[errorsKeys[0]][0],
          type: 'error',
        });
      });

      return creditNoteData;
    },

    /**
     * Delete the product from creditNote.
     */
    destroy(creditNoteProductId) {
      this.destroyLoading = creditNoteProductId;
      creditNoteProductService.destroy(this.bill.id, creditNoteProductId)
      .then(() => {
        this.destroyLoading = false;
      }).catch(() => {
        this.destroyLoading = false;
      });
    },

    getResources() {
      this.ready = false;
      Promise.all([
        creditNoteProductService.all(this.bill.id),
        taxService.all(),
        productDepositService.all(),
        billProductService.all(this.bill.creditNoteSource),
        orderProductService.all(this.order.id),
      ]).then((data) => {
        this.ready = true;
       // // console.log(data);
      }).catch((error) => {
        this.ready = true;
        // console.log(error);
      });
    },
    searchMethod(search, field) {
      if (this.bill.creditNoteSource <= 0) {
        return Promise.all([
          orderProductService.search(this.order.id, { search }),
        ]);
      }
      return Promise.all([
        billProductService.search(this.bill.creditNoteSource, { search }),
      ]);
    },
  },

  mounted() {
    this.getResources();
    Object.keys(this.form.fields).forEach((field) => {
      if (field !== 'quantity' && field !== 'billProductId') {
        this.form.disableField(field);
      }
      if (this.order.orderType !== 'EDI' && field === 'price') {
        this.form.enableField(field);
      }
    });
  },

  components: {
    VFormTable: require('./../formTable/formTable.vue'),
    VTotalsTable: require('./../totalsTable/totalsTable.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/components/creditNoteProductsTable/creditNoteProductsTable.js